// Import the compareValues function to handle different comparison operations
import compareValues from "./compareValues";

/**
 * Evaluates a condition on an item based on the provided operator and value.
 * The function ensures that the condition is an object, checks if the operator is supported,
 * and then delegates the comparison to compareValues.
 * 
 * @param {any} item - The value or object to which the condition will be applied.
 * @param {Object} condition - An object representing the condition with operator and value, e.g., {">": 10}.
 * @param {string} type - The type of the value (e.g., "number", "string", or "date").
 * @returns {boolean} - Returns `true` if the condition is valid and the comparison is successful, otherwise `true` for invalid conditions or errors.
 * 
 * Example Usage:
 * evaluateCondition(5, {">": 3}, "number");    // true
 * evaluateCondition("apple", {">=": "banana"}, "string");   // false
 * evaluateCondition("2023-11-01", {"<": "2023-12-01"}, "date"); // true
 */
export default function evaluateCondition(item, condition, type) {
  try {
    // Ensure condition is an object and has at least one entry
    if (
      typeof condition !== "object" ||
      condition === null ||
      Object.keys(condition).length === 0
    ) {
      console.warn("Invalid condition:", condition);
      return true; // Treat as true for invalid condition
    }

    // Destructure the operator and value from the condition object
    const [operator, value] = Object.entries(condition)[0];

    // Check for valid operator
    if (!["<", ">", "<=", ">=", "==", "!=", "<>"].includes(operator)) {
      console.warn("Unsupported operator:", operator);
      return true; // Treat as true for unsupported operator
    }

    // Perform the comparison based on the operator
    switch (operator) {
      case "<":
      case ">":
      case "<=":
      case ">=":
      case "==":
      case "!=":
      case "<>": {
        // Call compareValues to compare the item with the target value
        return compareValues(operator, type, item, value);
      }
      default:
        return true; // If the operator is not recognized, treat it as true
    }
  } catch (error) {
    console.error("Error in evaluateCondition function:", error);
    return true; // Fallback return value on error
  }
}
